/* ============================================================================
 * cart-progress-bar.js  ── ADDED (new file)
 * ----------------------------------------------------------------------------
 * Live controller for the cart-drawer shipping/rewards progress bar
 * (snippets/cart-progress-bar.liquid).
 *
 * It does NOT touch or replace any existing cart logic. It simply listens to
 * the cart events the theme already emits and re-paints the bar:
 *   - MinimogTheme.pubSubEvents.cartUpdate  (qty change, AJAX add-to-cart, …)
 *   - "cart:updated"  / "product-ajax:added" DOM events
 *   - MinimogTheme.pubSubEvents.openCartDrawer (refresh + animate on open)
 *
 * On every signal it fetches the authoritative /cart.js total and animates the
 * bar via a CSS width transition, so the fill stays smooth across all flows.
 * The element lives outside the drawer's AJAX-replaced selectors, so it
 * persists (and animates) instead of being re-rendered from scratch.
 * ========================================================================== */
(function () {
  "use strict";

  // Icons span 0–90% of the track; the final 10% is the black "tail".
  // Must stay in sync with the icon position maths in cart-progress-bar.liquid.
  var ICON_SPAN = 90;

  class CartProgressBar extends HTMLElement {
    constructor() {
      super();
      this.fill = this.querySelector("[data-cpb-fill]");
      this.messageEl = this.querySelector("[data-cpb-message]");
      this.markers = Array.from(this.querySelectorAll("[data-cpb-marker]"));

      try {
        this.milestones = JSON.parse(this.dataset.milestones || "[]");
      } catch (e) {
        this.milestones = [];
      }

      this.moneyFormat = this.dataset.moneyFormat || "₹{{amount}}";
      // Merchant-configurable message templates (with [amount] / [reward] tokens).
      this.msgRemaining = this.dataset.msgRemaining || "Add [amount] more to get [reward]";
      this.msgNext = this.dataset.msgNext || "Add [amount] more to unlock [reward]";
      this.showNext = this.dataset.showNext !== "false";
      this.total = parseInt(this.dataset.cartTotal, 10) || 0;
      this.rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

      // Debounced fetch so rapid events (qty spam) collapse into one refresh.
      this._debouncedRefresh = this._debounce(() => this.refresh(false), 150);
    }

    connectedCallback() {
      // Paint the current state with a fill-in animation from zero.
      this.render(this.total, true);
      this._bindEvents();
    }

    disconnectedCallback() {
      this._unbindEvents();
    }

    /* ── Event wiring ──────────────────────────────────────────────────── */
    _bindEvents() {
      this._onDomUpdate = () => this._debouncedRefresh();
      document.addEventListener("cart:updated", this._onDomUpdate);
      document.addEventListener("product-ajax:added", this._onDomUpdate);

      if (window.MinimogEvents && window.MinimogTheme && window.MinimogTheme.pubSubEvents) {
        const events = window.MinimogTheme.pubSubEvents;
        this._unsubCartUpdate = window.MinimogEvents.subscribe(events.cartUpdate, () => this._debouncedRefresh());
        // Re-animate from zero whenever the drawer is opened.
        this._unsubOpen = window.MinimogEvents.subscribe(events.openCartDrawer, () => this.refresh(true));
      }
    }

    _unbindEvents() {
      document.removeEventListener("cart:updated", this._onDomUpdate);
      document.removeEventListener("product-ajax:added", this._onDomUpdate);
      if (typeof this._unsubCartUpdate === "function") this._unsubCartUpdate();
      if (typeof this._unsubOpen === "function") this._unsubOpen();
    }

    /* ── Data ──────────────────────────────────────────────────────────── */
    refresh(animateFromZero) {
      return fetch(this.rootUrl + "cart.js", { headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .then((cart) => {
          this.total = cart.total_price;
          this.render(cart.total_price, animateFromZero === true);
        })
        .catch(() => {});
    }

    /* ── Rendering ─────────────────────────────────────────────────────── */
    render(total, animateFromZero) {
      // Icons sit at 30/60/90% of the track (ICON_SPAN). The fill lands exactly
      // on each icon while progressing, then runs to 100% (black "tail") once
      // every milestone is unlocked — matching the reference layout.
      const last = this.milestones[this.milestones.length - 1];
      const isComplete = !!last && total >= last.amount;
      const pct = this._percent(total);
      const visual = isComplete ? 100 : pct * (ICON_SPAN / 100);

      if (this.fill) {
        if (animateFromZero) {
          this.fill.style.width = "0%";
          // Double rAF guarantees the browser registers the 0% start before
          // we set the target, so the CSS transition actually animates.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.fill.style.width = visual + "%";
            });
          });
        } else {
          this.fill.style.width = visual + "%";
        }
      }

      // Toggle reached/unlocked state on each marker.
      this.markers.forEach((marker, i) => {
        const amount = this.milestones[i] ? this.milestones[i].amount : Infinity;
        marker.classList.toggle("is-reached", total >= amount);
      });

      if (this.messageEl) this.messageEl.innerHTML = this._message(total);

      this.classList.toggle("is-complete", isComplete);
    }

    // Equal-segment fill: each milestone owns 1/N of the bar width.
    _percent(total) {
      const n = this.milestones.length;
      if (!n) return 0;
      const seg = 100 / n;
      let lower = 0;
      for (let i = 0; i < n; i++) {
        const upper = this.milestones[i].amount;
        if (total < upper) {
          const within = (total - lower) / (upper - lower);
          return Math.max(0, Math.min(100, (i + within) * seg));
        }
        lower = upper;
      }
      return 100;
    }

    // Mirrors the Liquid message logic: highest unlocked reward + next-step hint.
    // Uses the merchant-configured templates with [amount] / [reward] tokens.
    _message(total) {
      const ms = this.milestones;
      if (!ms.length) return "";

      let reachedIdx = -1;
      for (let i = 0; i < ms.length; i++) {
        if (total >= ms[i].amount) reachedIdx = i;
      }

      if (reachedIdx === -1) {
        const remaining = ms[0].amount - total;
        return (
          '<span class="cart-progress__msg-text">' +
          this._template(this.msgRemaining, remaining, ms[0].reward) +
          "</span>"
        );
      }

      // All milestones unlocked — single compact line (matches reference).
      if (reachedIdx === ms.length - 1) {
        return '<span class="cart-progress__msg-text cart-progress__msg-text--unlocked">You have unlocked all rewards!</span>';
      }

      let html =
        '<span class="cart-progress__msg-text cart-progress__msg-text--unlocked">' +
        ms[reachedIdx].unlocked +
        "</span>";

      const next = ms[reachedIdx + 1];
      if (this.showNext && next) {
        const remaining = next.amount - total;
        html += '<span class="cart-progress__msg-next">' + this._template(this.msgNext, remaining, next.reward) + "</span>";
      }
      return html;
    }

    // Fills a message template, wrapping the amount and reward in <strong>.
    _template(tpl, cents, reward) {
      return String(tpl)
        .replace("[amount]", "<strong>" + this._money(cents) + "</strong>")
        .replace("[reward]", "<strong>" + reward + "</strong>");
    }

    /* ── Helpers ───────────────────────────────────────────────────────── */
    _money(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }
      // Fallback consistent with the theme's other money formatters.
      return "₹" + Math.round(cents / 100).toLocaleString("en-IN");
    }

    _debounce(fn, wait) {
      let t;
      return function () {
        clearTimeout(t);
        t = setTimeout(fn, wait);
      };
    }
  }

  if (!customElements.get("cart-progress-bar")) {
    customElements.define("cart-progress-bar", CartProgressBar);
  }
})();
